import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import HeaderLogged from './components/header_logged';
import Container from './components/container';
import api from './api/client';
import Notification from './components/Notification';
import HistoryPopup from './components/HistoryPopup';
import ChangesPopup from './components/ChangesPopup'; 
import UnscannedBagsPopup from './components/UnscannedBagsPopup';
import { API_URL } from './config';

const ScannersSeedStockComparsion = () => {
    const [form1State, setForm1State] = useState({
        seedsID: '',
        bagnumid: '',
        combinedSeedsName: '',
        bagNumbers: [],
        isLabelVisible: false
    });
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [logData, setLogData] = useState([]);
    const [expandedSeeds, setExpandedSeeds] = useState(new Set());
    const [form1BagNumError, setForm1BagNumError] = useState('');
    const [checkboxStates, setCheckboxStates] = useState({});
    const [containers, setContainers] = useState([]);
    const [editingFields, setEditingFields] = useState({});
    const [inputValues, setInputValues] = useState({});
    const [editedValues, setEditedValues] = useState({});
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [scanPopup, setScanPopup] = useState({ isOpen: false, id: '', bagnumid: '' });
    const [scanInput, setScanInput] = useState('');
    const [missingBagPopup, setMissingBagPopup] = useState({
        isOpen: false,
        seedsID: '',
        bagnumid: '',
        containerID: '',
        lot: '',
        currentWeight: '',
        testGermRate: '',
        testGermDate: ''
    });
    const [missingBagsQueue, setMissingBagsQueue] = useState([]);
    const [currentBagIndex, setCurrentBagIndex] = useState(0);
    const [unscannedPopupOpen, setUnscannedPopupOpen] = useState(false);
    const [historyPopup, setHistoryPopup] = useState({
        isOpen: false,
        seedsID: '',
        bagnumid: '',
        queryParams: ''
    });
    const [changesPopup, setChangesPopup] = useState({
        isOpen: false,
        date: ''
    });
    const [usedBags, setUsedBags] = useState({});
    const [scrollToKey, setScrollToKey] = useState(null);
    const seedRowRefs = useRef({});
    const [expandedWholeSeeds, setExpandedWholeSeeds] = useState(new Set());
    const [wholeSeedsData, setWholeSeedsData] = useState({});

    const formatWeight = (weight) => {
        if (weight === null || weight === undefined || weight === '') return '0.00';
        const num = Number(weight);
        return isNaN(num) ? '0.00' : num.toFixed(2);
    };

    const toggleUsedBags = async (date, seedsID) => {
        const key = `${date}-${seedsID}`;
        if (usedBags[key]) {
            setUsedBags(prev => {
                const newUsedBags = { ...prev };
                delete newUsedBags[key];
                return newUsedBags;
            });
        } else {
            try {
                const [month, day, year] = date.split('/');
                const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                const response = await api.get(`/api/used_bags/${formattedDate}/${seedsID}`);
                setUsedBags(prev => ({
                    ...prev,
                    [key]: response.data
                }));
            } catch (error) {
                console.error('Error fetching used bags:', error);
                setNotification({ message: 'Failed to fetch used bags.', type: 'error' });
            }
        }
    };

    const isValidBagNumID = (bagnumid) => /^[A-Za-z]/.test(bagnumid);

    const openUnscannedPopup = () => {
        setUnscannedPopupOpen(true);
    };

    const historyConfig = {
        endpoint: `${API_URL}/api/seedslocationwh/history`,
        columns: [
            { key: 'modifiedAt', label: 'Date/Time', type: 'datetime' },
            { key: 'modifiedBy', label: 'Modified By' },
            { key: 'oldWeight', label: 'Old Weight' },
            { key: 'newWeight', label: 'New Weight' }
        ]
    };

    useEffect(() => {
        const fetchLogData = async () => {
            try {
                const response = await api.get(`/api/seedslocationwh_log`);
                setLogData(response.data);
                const initialCheckboxStates = response.data.reduce((acc, bag) => {
                    acc[bag.id || `${bag.seedsID}-${bag.bagnumid}`] = !!bag.confirmed;
                    return acc;
                }, {});
                setCheckboxStates(initialCheckboxStates);
            } catch (error) {
                console.error('Error fetching log data:', error);
                setNotification({ message: 'Failed to load history data.', type: 'error' });
            }
        };

        const fetchContainers = async () => {
            try {
                const response = await api.get(`/api/containers-seeds-check`);
                setContainers(response.data || []);
            } catch (error) {
                console.error('Error fetching containers:', error);
                setContainers([]);
            }
        };

        const fetchEmployees = async () => {
            try {
                const response = await api.get(`/api/employeenames-check-seeds`);
                setEmployees(response.data || []);
            } catch (error) {
                console.error('Error fetching employees:', error);
                setNotification({ message: 'Failed to load employee names.', type: 'error' });
            }
        };

        fetchLogData();
        fetchContainers();
        fetchEmployees();
    }, []);

    useEffect(() => {
        if (!scrollToKey) return;
        const el = seedRowRefs.current[scrollToKey];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setScrollToKey(null);
        }
    }, [scrollToKey, expandedSeeds]);

    const getDateKeyFromData = (data, targetSeedsID) => {
        const grouped = data.reduce((acc, record) => {
            const utcDate = new Date(record.inventorization_date);
            const m = (utcDate.getUTCMonth() + 1).toString().padStart(2, '0');
            const d = utcDate.getUTCDate().toString().padStart(2, '0');
            const y = utcDate.getUTCFullYear();
            const date = `${m}/${d}/${y}`;
            if (!acc[date]) acc[date] = new Set();
            acc[date].add(String(record.seedsID));
            return acc;
        }, {});
        return Object.keys(grouped)
            .sort((a, b) => new Date(b) - new Date(a))
            .find(date => grouped[date].has(String(targetSeedsID))) || null;
    };

    const groupedLogData = logData.reduce((acc, record) => {
        const utcDate = new Date(record.inventorization_date);
        const year = utcDate.getUTCFullYear();
        const month = (utcDate.getUTCMonth() + 1).toString().padStart(2, '0');
        const day = utcDate.getUTCDate().toString().padStart(2, '0');
        const date = `${month}/${day}/${year}`;

        const { combinedSeedsName, ...bagData } = record;
        if (!acc[date]) {
            acc[date] = {};
        }
        if (!acc[date][record.seedsID]) {
            acc[date][record.seedsID] = { combinedSeedsName, bags: [] };
        }
        acc[date][record.seedsID].bags.push(bagData);
        return acc;
    }, {});

    const toggleDateExpansion = (date) => {
        const newExpanded = new Set(expandedSeeds);
        if (newExpanded.has(date)) {
            newExpanded.delete(date);
        } else {
            newExpanded.add(date);
        }
        setExpandedSeeds(newExpanded);
    };

    const toggleSeedExpansion = (date, seedsID) => {
        const key = `${date}-${seedsID}`;
        const newExpanded = new Set(expandedSeeds);
        if (newExpanded.has(key)) {
            newExpanded.delete(key);
        } else {
            newExpanded.add(key);
        }
        setExpandedSeeds(newExpanded);
    };

    const openChangesPopup = (date) => {
        setChangesPopup({ isOpen: true, date });
    };

    const toApiDate = (uiDate) => {
        const [m, d, y] = uiDate.split('/');
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    };

    const fetchWholeSeedsData = async (date) => {
        setWholeSeedsData(prev => ({ ...prev, [date]: { loading: true, scanned: [], notScanned: [] } }));
        try {
            const response = await api.get(`/api/seeds-daily-snapshot/${toApiDate(date)}`);
            const scanned = response.data.filter(r => r.wasScanned === 1);
            const notScanned = response.data.filter(r => r.wasScanned === 0);
            setWholeSeedsData(prev => ({ ...prev, [date]: { loading: false, scanned, notScanned } }));
        } catch (error) {
            console.error('Error fetching whole seeds snapshot:', error);
            setWholeSeedsData(prev => ({ ...prev, [date]: { loading: false, scanned: [], notScanned: [] } }));
        }
    };

    const toggleWholeSeedsExpansion = (date) => {
        const newExpanded = new Set(expandedWholeSeeds);
        if (newExpanded.has(date)) {
            newExpanded.delete(date);
        } else {
            newExpanded.add(date);
            if (!wholeSeedsData[date]) {
                fetchWholeSeedsData(date);
            }
        }
        setExpandedWholeSeeds(newExpanded);
    };

    const handleForm1Change = async (e) => {
        const { name, value } = e.target;
        if (name === 'bagnumid') {
            if (!isValidBagNumID(value)) {
                setForm1BagNumError('Invalid Bag Number');
            } else {
                setForm1BagNumError('');
            }
        }

        setForm1State((prevState) => ({
            ...prevState,
            [name]: value
        }));

        if (name === 'seedsID' && value) {
            try {
                const [seedsResponse, bagsResponse] = await Promise.all([
                    api.get(`/api/combinedSeedsName/${value}`),
                    api.get(`/api/seedsbags-seedscheck/${value}`)
                ]);
                setForm1State((prevState) => ({
                    ...prevState,
                    combinedSeedsName: seedsResponse.data.combinedSeedsName || '',
                    bagNumbers: bagsResponse.data.bagNumbers || [],
                    isLabelVisible: !!seedsResponse.data.combinedSeedsName
                }));
            } catch (error) {
                console.error('Error fetching seeds data:', error);
                setForm1State((prevState) => ({
                    ...prevState,
                    combinedSeedsName: '',
                    bagNumbers: [],
                    isLabelVisible: false
                }));
                setNotification({ message: 'Failed to fetch seeds or bag data.', type: 'error' });
            }
        } else if (name === 'seedsID' && !value) {
            setForm1State((prevState) => ({
                ...prevState,
                combinedSeedsName: '',
                bagNumbers: [],
                isLabelVisible: false
            }));
        }
    };

    const handleMissingBagChange = (e) => {
        const { name, value } = e.target;
        setMissingBagPopup((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleCheckboxChange = (bagId) => {
        setCheckboxStates((prev) => ({
            ...prev,
            [bagId]: !prev[bagId]
        }));
    };

    const getDisplayValue = (bag, fieldName) => {
        const bagId = bag.id || `${bag.seedsID}-${bag.bagnumid}`;
        let value;
        if (editedValues[bagId]?.[fieldName] !== undefined) {
            value = editedValues[bagId][fieldName];
        } else {
            value = bag[fieldName];
        }

        if (fieldName === 'old_weight') {
            return formatWeight(value);
        }
        
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        return value;
    };

    const getContainerName = (bag) => {
        const bagId = bag.id || `${bag.seedsID}-${bag.bagnumid}`;
        const editedContainerID = editedValues[bagId]?.containerID;
        if (editedContainerID) {
            const container = containers.find(c => c.containerID === editedContainerID);
            return container ? container.containerName : '-';
        }
        return bag.containerName || '-';
    };

    const startEditing = (bagId, fieldName) => {
        const bag = logData.find(b => (b.id || `${b.seedsID}-${b.bagnumid}`) === bagId) ||
                    Object.values(usedBags).flat().find(b => (b.id || `${b.seedsID}-${b.bagnumid}`) === bagId);
        if (!bag) {
            console.error(`Bag with ID ${bagId} not found`);
            setNotification({ message: 'Cannot edit: Bag not found.', type: 'error' });
            return;
        }
        let currentValue = editedValues[bagId]?.[fieldName] ?? bag[fieldName];
        if (fieldName === 'old_germtestdate' && currentValue) {
            currentValue = new Date(currentValue).toISOString().split('T')[0];
        }
        setEditingFields({ ...editingFields, [`${bagId}-${fieldName}`]: true });
        setInputValues({ ...inputValues, [`${bagId}-${fieldName}`]: currentValue || '' });
    };

    const saveEdit = async (bagId, fieldName, value) => {
        const bag = logData.find(b => (b.id || `${b.seedsID}-${b.bagnumid}`) === bagId) ||
                    Object.values(usedBags).flat().find(b => (b.id || `${b.seedsID}-${b.bagnumid}`) === bagId);
        if (!bag) {
            setNotification({ message: 'Cannot edit: Bag not found.', type: 'error' });
            return;
        }

        const finalValue = value !== undefined ? value : inputValues[`${bagId}-${fieldName}`];

        setEditedValues(prev => ({
            ...prev,
            [bagId]: {
                ...prev[bagId],
                [fieldName]: finalValue
            }
        }));
        setEditingFields({ ...editingFields, [`${bagId}-${fieldName}`]: false });

        try {
            const editedFields = { ...editedValues[bagId], [fieldName]: finalValue };
            await api.put(`/api/seedsinvscannercomparsion/${bag.seedsID}/${bag.bagnumid}`, {
                seedsID: bag.seedsID,
                bagnumid: bag.bagnumid,
                containerID: editedFields.containerID,
                lot: editedFields.old_lot,
                weight: editedFields.old_weight,
                germtestrate: editedFields.old_germtestrate,
                germtestdate: editedFields.old_germtestdate,
                wasScanned: bag.wasScanned
            });
            setNotification({ message: 'Record updated successfully!', type: 'success' });
            const response = await api.get(`/api/seedslocationwh_log`);
            setLogData(response.data);
        } catch (error) {
            console.error('Error updating record:', error);
            setNotification({ message: 'Failed to update record.', type: 'error' });
        }
    };

    const handleConfirm = async (date, seedsID) => {
        if (!selectedEmployee) {
            setNotification({ message: 'Please select an employee to confirm.', type: 'error' });
            return;
        }

        try {
            const allBags = [
                ...groupedLogData[date][seedsID].bags,
                ...(usedBags[`${date}-${seedsID}`] || [])
            ];
            const confirmations = allBags
                .filter(bag => {
                    const bagId = bag.id || `${bag.seedsID}-${bag.bagnumid}`;
                    return checkboxStates[bagId];
                })
                .map(bag => ({
                    id: bag.id || null,
                    confirmed: true,
                    editedFields: editedValues[bag.id || `${bag.seedsID}-${bag.bagnumid}`] || {},
                    whoConfirmed: Number(selectedEmployee),
                    seedsID: bag.seedsID,
                    bagnumid: bag.bagnumid
                }));

            if (confirmations.length === 0) {
                setNotification({ message: 'No bags selected for confirmation.', type: 'error' });
                return;
            }

            await api.put(`/api/seedslocationwh_log/confirm`, { confirmations });
            setNotification({ message: 'Selected bags confirmed successfully!', type: 'success' });

            const response = await api.get(`/api/seedslocationwh_log`);
            setLogData(response.data);
            const updatedCheckboxStates = response.data.reduce((acc, bag) => {
                acc[bag.id || `${bag.seedsID}-${bag.bagnumid}`] = !!bag.confirmed;
                return acc;
            }, {});
            setCheckboxStates(updatedCheckboxStates);
            setEditedValues(prev => {
                const newEditedValues = { ...prev };
                allBags.forEach(bag => {
                    const bagId = bag.id || `${bag.seedsID}-${bag.bagnumid}`;
                    if (checkboxStates[bagId]) {
                        delete newEditedValues[bagId];
                    }
                });
                return newEditedValues;
            });
            setSelectedEmployee('');
        } catch (error) {
            console.error('Error confirming bags:', error);
            const errorMessage = error.response?.data?.error || 'Failed to confirm bags.';
            setNotification({ message: errorMessage, type: 'error' });
        }
    };

    const handleForm1Submit = async (e) => {
        e.preventDefault();
        const { seedsID, bagnumid } = form1State;
        const bagNumbers = parseBagNumbers(bagnumid);

        if (bagNumbers.length === 0) {
            setForm1BagNumError('Please enter at least one valid Bag Number.');
            return;
        }

        let hasError = false;
        const missingBags = [];

        for (const bagNumber of bagNumbers) {
            if (!isValidBagNumID(bagNumber)) {
                setForm1BagNumError(`Invalid Bag Number: ${bagNumber}.`);
                hasError = true;
                continue;
            }

            try {
                const response = await api.get(`/api/seedsinvscannercomparsion/${seedsID}/${bagNumber}`);
                if (response.data && response.data.exists) {
                    await api.put(`/api/seedsinvscannercomparsion/${seedsID}/${bagNumber}`, {
                        seedsID,
                        bagnumid: bagNumber,
                        wasScanned: 'Yes'
                    });
                } else {
                    missingBags.push({ seedsID, bagnumid: bagNumber });
                }
            } catch (err) {
                console.error(`Error processing bag ${bagNumber}:`, err);
                setForm1BagNumError(`Failed to process bag ${bagNumber}.`);
                hasError = true;
            }
        }

        if (missingBags.length > 0) {
            setMissingBagsQueue(missingBags);
            setCurrentBagIndex(0);
            setMissingBagPopup({
                isOpen: true,
                seedsID: missingBags[0].seedsID,
                bagnumid: missingBags[0].bagnumid,
                containerID: '',
                lot: '',
                currentWeight: '',
                testGermRate: '',
                testGermDate: ''
            });
        }

        if (!hasError && missingBags.length === 0) {
            setNotification({ message: 'All records processed successfully!', type: 'success' });
            const scannedSeedsID = seedsID;
            setForm1State({ seedsID: '', bagnumid: '', combinedSeedsName: '', bagNumbers: [], isLabelVisible: false });
            const response = await api.get(`/api/seedslocationwh_log`);
            setLogData(response.data);
            const targetDate = getDateKeyFromData(response.data, scannedSeedsID);
            if (targetDate) {
                const key = `${targetDate}-${scannedSeedsID}`;
                setExpandedSeeds(new Set([targetDate, key]));
                setScrollToKey(key);
                fetchWholeSeedsData(targetDate);
            }
        }
    };

    const handleMissingBagSubmit = async (e) => {
        e.preventDefault();
        const { seedsID, bagnumid, containerID, lot, currentWeight, testGermRate, testGermDate } = missingBagPopup;
        const { combinedSeedsName } = form1State;

        if (!containerID || !lot || !currentWeight || !testGermRate || !testGermDate || !combinedSeedsName) {
            setNotification({ message: 'All fields are required, including Seeds Name.', type: 'error' });
            return;
        }

        try {
            const payload = {
                seedsID,
                bagnumid,
                containerID: Number(containerID),
                lot,
                currentWeight: Number(currentWeight),
                seedsName: combinedSeedsName,
                germtestrate: Number(testGermRate),
                germtestdate: testGermDate
            };
            await api.post(`/api/seedsinvscanner`, payload);
            await api.put(`/api/seedsinvscannercomparsion/${seedsID}/${bagnumid}`, {
                seedsID,
                bagnumid,
                wasScanned: 'Yes'
            });
            setNotification({ message: `Bag ${bagnumid} added and scanned successfully!`, type: 'success' });

            const nextIndex = currentBagIndex + 1;
            if (nextIndex < missingBagsQueue.length) {
                setCurrentBagIndex(nextIndex);
                setMissingBagPopup({
                    isOpen: true,
                    seedsID: missingBagsQueue[nextIndex].seedsID,
                    bagnumid: missingBagsQueue[nextIndex].bagnumid,
                    containerID: '',
                    lot: '',
                    currentWeight: '',
                    testGermRate: '',
                    testGermDate: ''
                });
            } else {
                setMissingBagPopup({
                    isOpen: false,
                    seedsID: '',
                    bagnumid: '',
                    containerID: '',
                    lot: '',
                    currentWeight: '',
                    testGermRate: '',
                    testGermDate: ''
                });
                setMissingBagsQueue([]);
                setCurrentBagIndex(0);
                const scannedSeedsID = seedsID;
                setForm1State({ seedsID: '', bagnumid: '', combinedSeedsName: '', bagNumbers: [], isLabelVisible: false });
                const response = await api.get(`/api/seedslocationwh_log`);
                setLogData(response.data);
                const targetDate = getDateKeyFromData(response.data, scannedSeedsID);
                if (targetDate) {
                    const key = `${targetDate}-${scannedSeedsID}`;
                    setExpandedSeeds(new Set([targetDate, key]));
                    setScrollToKey(key);
                    fetchWholeSeedsData(targetDate);
                }
            }
        } catch (error) {
            console.error('Error adding missing bag:', error.response?.data || error.message);
            setNotification({
                message: 'Failed to add bag: ' + (error.response?.data?.message || 'Unknown error'),
                type: 'error'
            });
        }
    };

    const closeMissingBagPopup = () => {
        setMissingBagPopup({
            isOpen: false,
            seedsID: '',
            bagnumid: '',
            containerID: '',
            lot: '',
            currentWeight: '',
            testGermRate: '',
            testGermDate: ''
        });
        setMissingBagsQueue([]);
        setCurrentBagIndex(0);
    };

    const openScanPopup = async (id, bagnumid, seedsID) => {
        try {
            let bagId = id;
            if (!seedsID) {
                throw new Error('Missing seedsID parameter');
            }
            const response = await api.post(`/api/seedslocationwh_log/prelog/${seedsID}/${bagnumid}`);
            bagId = response.data.id;

            if (!bagId) {
                setNotification({ message: 'Cannot scan: Bag not found in inventory.', type: 'error' });
                return;
            }

            const logResponse = await api.get(`/api/seedslocationwh_log`);
            setLogData(logResponse.data);

            setScanPopup({ isOpen: true, id: bagId, bagnumid });
            setScanInput('');
        } catch (error) {
            console.error('Error pre-logging bag:', error);
            setNotification({ message: 'Failed to prepare bag for scanning.', type: 'error' });
        }
    };

    const closeScanPopup = () => {
        setScanPopup({ isOpen: false, id: '', bagnumid: '' });
        setScanInput('');
    };

    const handleScanSubmit = async (e) => {
        e.preventDefault();
        if (!isValidBagNumID(scanInput)) {
            setNotification({ message: 'Invalid Bag Number scanned.', type: 'error' });
            return;
        }
        if (scanInput !== scanPopup.bagnumid) {
            setNotification({ message: 'Scanned Bag Number does not match.', type: 'error' });
            return;
        }

        try {
            await api.put(`/api/seedslocationwh_log/scanned-status-update/${scanPopup.id}`, {
                wasScanned: 'Yes'
            });
            setNotification({ message: 'Bag scanned successfully!', type: 'success' });
            const response = await api.get(`/api/seedslocationwh_log`);
            setLogData(response.data);
            closeScanPopup();
        } catch (error) {
            console.error('Error scanning bag:', error);
            setNotification({ message: 'Failed to scan bag.', type: 'error' });
        }
    };

    const handleViewHistory = (bag) => {
        const keyFieldsConfig = ['seedsID', 'bagnumid'];
        const queryParams = keyFieldsConfig
            .map(key => `${key}=${encodeURIComponent(bag[key])}`)
            .join('&');
        
        setHistoryPopup({
            isOpen: true,
            seedsID: bag.seedsID,
            bagnumid: bag.bagnumid,
            queryParams
        });
    };

    const closeHistoryPopup = () => {
        setHistoryPopup({
            isOpen: false,
            seedsID: '',
            bagnumid: '',
            queryParams: ''
        });
    };

    const handleTrashClick = (bagId) => {
        const confirmReset = window.confirm('Are you sure you want to set the weight of this bag to 0?');
        if (confirmReset) {
            setInputValues(prev => ({
                ...prev,
                [`${bagId}-old_weight`]: '0.00'
            }));
            saveEdit(bagId, 'old_weight', '0.00');
        }
    };

    const handleRollback = (logID) => {
        const confirmRollback = window.confirm('Are you sure you want to rollback the changes for this bag?');
        if (confirmRollback) {
            rollbackChanges(logID);
        }
    };

    const rollbackChanges = async (logID) => {
        try {
            await api.post(`/api/seedslocationwh_log/rollback/${logID}`);
            setNotification({ message: 'Changes rolled back successfully!', type: 'success' });
            const response = await api.get(`/api/seedslocationwh_log`);
            setLogData(response.data);
            const updatedCheckboxStates = response.data.reduce((acc, bag) => {
                acc[bag.id || `${bag.seedsID}-${bag.bagnumid}`] = !!bag.confirmed;
                return acc;
            }, {});
            setCheckboxStates(updatedCheckboxStates);
        } catch (error) {
            console.error('Error rolling back changes:', error);
            setNotification({ message: 'Failed to rollback changes.', type: 'error' });
        }
    };

    const closeNotification = () => {
        setNotification({ message: '', type: '' });
    };

    const parseBagNumbers = (bagnumid) => {
        return bagnumid
            .split(/[\s,]+/)
            .map((item) => item.trim())
            .filter((item, index, self) => item && self.indexOf(item) === index);
    };

    const formatUSADate = (date) => {
        if (!date || isNaN(new Date(date))) return '-';
        const utcDate = new Date(date);
        const year = utcDate.getUTCFullYear();
        const month = (utcDate.getUTCMonth() + 1).toString().padStart(2, '0');
        const day = utcDate.getUTCDate().toString().padStart(2, '0');
        return `${month}/${day}/${year}`;
    };

    const getPendingBags = (bags, date, seedsID) => {
        return bags
            .filter(bag => {
                const bagId = bag.id || `${bag.seedsID}-${bag.bagnumid}`;
                return bag.wasScanned === 'No' || !checkboxStates[bagId];
            })
            .map(bag => {
                const bagId = bag.id || `${bag.seedsID}-${bag.bagnumid}`;
                const isScanned = bag.wasScanned === 'Yes';
                const isConfirmed = checkboxStates[bagId];
                return (
                    <span key={bagId} style={{ marginRight: '10px' }}>
                        {bag.bagnumid}
                        <FontAwesomeIcon
                            icon={faQrcode}
                            style={{ color: isScanned ? '#10b981' : '#ff0000', marginLeft: '5px' }}
                            title={isScanned ? 'Scanned' : 'Not Scanned'}
                        />
                        <FontAwesomeIcon
                            icon={faCheck}
                            style={{ color: isConfirmed ? '#10b981' : '#ff0000', marginLeft: '5px' }}
                            title={isConfirmed ? 'Confirmed' : 'Not Confirmed'}
                        />
                    </span>
                );
            });
    };

    return (
        <div className="scanners-inv-seeds-page-bg">
            <style>{`
                .inventorylogtable-container {
                    background-color: rgba(255, 255, 255, 0.95);
                    border-radius: 8px;
                    box-shadow: 0 4px 4px rgba(0, 0, 0, 0.1);
                    padding: 16px;
                    margin-top: 20px;
                    z-index: 10;
                    position: relative;
                }
                .inventorylogtable-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 14px;
                }
                .inventorylogtable-thead {
                    background-color: #f3f4f6;
                    color: #374151;
                }
                .inventorylogtable-th {
                    padding: 12px;
                    text-align: left;
                    border-bottom: 1px solid #e5e7eb;
                    font-weight: 600;
                }
                .inventorylogtable-td {
                    padding: 12px;
                    border-bottom: 1px solid #e5e7eb;
                }
                .inventorylogtable-row {
                    cursor: pointer;
                    position: relative;
                    z-index: 11;
                }
                .inventorylogtable-nested-table {
                    width: 100%;
                    background-color: #fff;
                    border-collapse: collapse;
                    margin-top: 2px;
                    margin-bottom: 8px;
                    z-index: 2;
                }
                .inventorylogtable-nested-thead {
                    background-color: #e5e7eb;
                }
                .edit-icon, .save-icon, .scan-icon, .trash-icon, .history-icon, .rollback-icon {
                    cursor: pointer;
                    margin-left: 5px;
                    transition: color 0.2s;
                }
                .edit-icon:hover, .save-icon:hover, .scan-icon:hover, .history-icon:hover, .rollback-icon:hover {
                    color: #10b981;
                }
                .inventorylogtable-no-history {
                    text-align: center;
                    padding: 24px;
                    color: #6b7280;
                    font-style: italic;
                }
                .inventorylogtable-expand-icon {
                    margin-right: 8px;
                    display: inline-block;
                    width: 16px;
                }
                .inventorylogtable-nested-container {
                    padding: 10px;
                    background-color: #f9fafb;
                    z-index: 2;
                }
                .inventorylogtable-heading {
                    font-size: 24px;
                    font-weight: 700;
                    margin-bottom: 16px;
                }
                .confirm-button-container {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    margin-top: 12px;
                }
                .confirm-button {
                    background-color: #10b981;
                    color: white;
                    padding: 8px 16px;
                    border-radius: 4px;
                    border: none;
                    cursor: pointer;
                    transition: background-color 0.2s;
                }
                .confirm-button:hover {
                    background-color: #059669;
                }
                .toggle-used-bags-button {
                    background-color: #6b7280;
                    color: white;
                    padding: 8px 16px;
                    border-radius: 4px;
                    border: none;
                    cursor: pointer;
                    transition: background-color 0.2s;
                    margin-bottom: 12px;
                }
                .toggle-used-bags-button:hover {
                    background-color: #4b5563;
                }
                .signature-container {
                    display: flex;
                    align-items: center;
                    margin-right: 16px;
                }
                .signature-container label {
                    margin-right: 8px;
                    font-weight: 600;
                    color: #374151;
                }
                .signature-container select {
                    appearance: none;
                    background-color: #ffffff;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    padding: 8px 32px 8px 12px;
                    font-size: 14px;
                    color: #374151;
                    cursor: pointer;
                    transition: border-color 0.2s, box-shadow 0.2s;
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
                    background-repeat: no-repeat;
                    background-position: right 12px center;
                    background-size: 16px;
                }
                .signature-container select:focus {
                    outline: none;
                    border-color: #10b981;
                    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
                }
                .signature-container select:hover {
                    border-color: #9ca3af;
                }
                .inventorylogtable-nested-table select {
                    appearance: none;
                    background-color: #ffffff;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    padding: 8px 32px 8px 12px;
                    font-size: 14px;
                    color: #374151;
                    width: 100%;
                    max-width: 200px;
                    cursor: pointer;
                    transition: border-color 0.2s, box-shadow 0.2s;
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
                    background-repeat: no-repeat;
                    background-position: right 12px center;
                    background-size: 16px;
                }
                .inventorylogtable-nested-table select:focus {
                    outline: none;
                    border-color: #10b981;
                    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
                }
                .inventorylogtable-nested-table select:hover {
                    border-color: #9ca3af;
                }
                .inventorylogtable-nested-table input[type="checkbox"] {
                    appearance: none;
                    width: 18px;
                    height: 18px;
                    border: 2px solid #d1d5db;
                    border-radius: 4px;
                    background-color: #ffffff;
                    cursor: pointer;
                    position: relative;
                    transition: border-color 0.2s, background-color 0.2s;
                }
                .inventorylogtable-nested-table input[type="checkbox"]:checked {
                    background-color: #10b981;
                    border-color: #10b981;
                }
                .inventorylogtable-nested-table input[type="checkbox"]:checked::after {
                    content: '\u2713';
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    color: #ffffff;
                    font-size: 12px;
                    font-weight: bold;
                }
                .inventorylogtable-nested-table input[type="checkbox"]:hover {
                    border-color: #9ca3af;
                }
                .inventorylogtable-nested-table input[type="text"],
                .inventorylogtable-nested-table input[type="number"],
                .inventorylogtable-nested-table input[type="date"] {
                    background-color: #ffffff;
                    border: 1px solid #d1d5db;
                    border-radius: 6px;
                    padding: 8px 12px;
                    font-size: 14px;
                    color: #374151;
                    width: 100%;
                    max-width: 200px;
                    transition: border-color 0.2s, box-shadow 0.2s;
                }
                .inventorylogtable-nested-table input[type="text"]:focus,
                .inventorylogtable-nested-table input[type="number"]:focus,
                .inventorylogtable-nested-table input[type="date"]:focus {
                    outline: none;
                    border-color: #10b981;
                    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
                }
                .inventorylogtable-nested-table input[type="text"]:hover,
                .inventorylogtable-nested-table input[type="number"]:hover,
                .inventorylogtable-nested-table input[type="date"]:hover {
                    border-color: #9ca3af;
                }
                .scannerform-columns {
                    display: flex;
                    gap: 20px;
                    flex-wrap: nowrap;
                }
                .scannerform-column {
                    flex: 1;
                    min-width: 250px;
                }
                .scannerform-column--bags {
                    background-color: #f9fafb;
                    padding: 16px;
                    border-radius: 8px;
                    border: 1px solid rgb(114, 114, 114);
                    max-height: 300px;
                    overflow-y: auto;
                }
                .scannerform-bags-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 14px;
                }
                .scannerform-bags-th {
                    padding: 8px;
                    text-align: left;
                    border-bottom: 1px solid #e5e7eb;
                    font-weight: 600;
                    background-color: #f3f4f6;
                    color: #374151;
                }
                .scannerform-bags-td {
                    padding: 8px;
                    border-bottom: 1px solid #e5e7eb;
                    color: #374151;
                }
                .scannerform-bags-empty {
                    color: #6b7280;
                    font-style: italic;
                    text-align: center;
                    padding: 8px;
                }
                .scannerform {
                    max-width: 800px;
                    margin-top: 150px;
                }
                .scan-popup, .missing-bag-popup {
                    position: fixed;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    background-color: #ffffff;
                    padding: 20px;
                    border-radius: 8px;
                    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
                    z-index: 1000;
                    width: 400px;
                }
                .scan-popup-overlay, .missing-bag-popup-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background-color: rgba(0, 0, 0, 0.5);
                    z-index: 999;
                }
                .scan-popup-title, .missing-bag-popup-title {
                    font-size: 18px;
                    font-weight: 600;
                    margin-bottom: 16px;
                }
                .scan-popup-input, .missing-bag-popup-input {
                    width: 100%;
                    padding: 8px;
                    border: 1px solid #d1d5db;
                    border-radius: 4px;
                    margin-bottom: 16px;
                }
                .scan-popup-buttons, .missing-bag-popup-buttons {
                    display: flex;
                    justify-content: flex-end;
                    gap: 8px;
                }
                .scan-popup-button, .missing-bag-popup-button {
                    padding: 8px 16px;
                    border-radius: 4px;
                    cursor: pointer;
                }
                .scan-popup-button--submit, .missing-bag-popup-button--submit {
                    background-color: #10b981;
                    color: white;
                    border: none;
                }
                .scan-popup-button--submit:hover, .missing-bag-popup-button--submit:hover {
                    background-color: #059669;
                }
                .scan-popup-button--cancel, .missing-bag-popup-button--cancel {
                    background-color: #e5e7eb;
                    color: #374151;
                    border: none;
                }
                .scan-popup-button--cancel:hover, .missing-bag-popup-button--cancel:hover {
                    background-color: #d1d5db;
                }
                .missing-bag-popup select {
                    width: 100%;
                    padding: 8px;
                    border: 1px solid #d1d5db;
                    border-radius: 4px;
                    margin-bottom: 16px;
                    appearance: none;
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'/%3E%3C/svg%3E");
                    background-repeat: no-repeat;
                    background-position: right 12px center;
                    background-size: 16px;
                }
                .missing-bag-popup select:focus {
                    outline: none;
                    border-color: #10b981;
                    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
                }
                .missing-bag-popup select:hover {
                    border-color: #9ca3af;
                }
                .fa-qrcode, .fa-eye, .fa-undo {color:rgb(72, 83, 100) }
                .used-bag {
                    background-color: rgb(211, 211, 211);
                }
                tr:hover td {
                    background:rgba(255, 255, 255, 0); 
                }
                tr:hover input {
                    background:rgba(255, 255, 255, 0); 
                }
                .used-bag:hover{
                    background-color: rgb(211, 211, 211);
                }
                .seeds-name-container {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    width: 100%;
                }
                .pending-bags {
                    font-size: 12px;
                    color: #374151;
                }
            `}</style>
            <Notification
                message={notification.message}
                type={notification.type}
                onClose={closeNotification}
            />
            {scanPopup.isOpen && (
                <>
                    <div className="scan-popup-overlay" onClick={closeScanPopup}></div>
                    <div className="scan-popup">
                        <h3 className="scan-popup-title">Scan Bag Number</h3>
                        <form onSubmit={handleScanSubmit}>
                            <input
                                type="text"
                                className="scan-popup-input"
                                value={scanInput}
                                onChange={(e) => setScanInput(e.target.value)}
                                placeholder="Scan bag number"
                                autoFocus
                            />
                            <div className="scan-popup-buttons">
                                <button type="button" className="scan-popup-button scan-popup-button--cancel" onClick={closeScanPopup}>
                                    Cancel
                                </button>
                                <button type="submit" className="scan-popup-button scan-popup-button--submit">
                                    Submit
                                </button>
                            </div>
                        </form>
                    </div>
                </>
            )}
            {missingBagPopup.isOpen && (
                <>
                    <div className="missing-bag-popup-overlay" onClick={closeMissingBagPopup}></div>
                    <div className="missing-bag-popup">
                        <h3 className="missing-bag-popup-title">Add New Bag</h3>
                        <form onSubmit={handleMissingBagSubmit}>
                            <div className="scannerform-inputgroup">
                                <label className="scannerform-label">Seeds ID</label>
                                <input
                                    className="missing-bag-popup-input"
                                    name="seedsID"
                                    value={missingBagPopup.seedsID}
                                    readOnly
                                />
                            </div>
                            <div className="scannerform-inputgroup">
                                <label className="scannerform-label">Bag Number</label>
                                <input
                                    className="missing-bag-popup-input"
                                    name="bagnumid"
                                    value={missingBagPopup.bagnumid}
                                    readOnly
                                />
                            </div>
                            <div className="scannerform-inputgroup">
                                <label className="scannerform-label">Seeds Name</label>
                                <input
                                    className="missing-bag-popup-input"
                                    value={form1State.combinedSeedsName}
                                    readOnly
                                />
                            </div>
                            <div className="scannerform-inputgroup">
                                <label className="scannerform-label">Container</label>
                                <select
                                    name="containerID"
                                    value={missingBagPopup.containerID}
                                    onChange={handleMissingBagChange}
                                    required
                                >
                                    <option value="">Select Container</option>
                                    {containers.map((container) => (
                                        <option key={container.containerID} value={container.containerID}>
                                            {container.containerName}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="scannerform-inputgroup">
                                <label className="scannerform-label">Lot</label>
                                <input
                                    className="missing-bag-popup-input"
                                    name="lot"
                                    value={missingBagPopup.lot}
                                    onChange={handleMissingBagChange}
                                    placeholder="Example: C1, C2"
                                    required
                                />
                            </div>
                            <div className="scannerform-inputgroup">
                                <label className="scannerform-label">Current Weight (lb)</label>
                                <input
                                    className="missing-bag-popup-input"
                                    name="currentWeight"
                                    type="number"
                                    value={missingBagPopup.currentWeight}
                                    onChange={handleMissingBagChange}
                                    placeholder="Example: 5.2"
                                    required
                                />
                            </div>
                            <div className="scannerform-inputgroup">
                                <label className="scannerform-label">Germination Rate</label>
                                <input
                                    className="missing-bag-popup-input"
                                    name="testGermRate"
                                    type="number"
                                    value={missingBagPopup.testGermRate}
                                    onChange={handleMissingBagChange}
                                    placeholder="Example: 85 (without %)"
                                    required
                                />
                            </div>
                            <div className="scannerform-inputgroup">
                                <label className="scannerform-label">Germination Test Date</label>
                                <input
                                    className="missing-bag-popup-input"
                                    name="testGermDate"
                                    type="date"
                                    value={missingBagPopup.testGermDate}
                                    onChange={handleMissingBagChange}
                                    required
                                />
                            </div>
                            <div className="missing-bag-popup-buttons">
                                <button
                                    type="button"
                                    className="missing-bag-popup-button missing-bag-popup-button--cancel"
                                    onClick={closeMissingBagPopup}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="missing-bag-popup-button missing-bag-popup-button--submit"
                                    >
                                    Add Bag
                                </button>
                            </div>
                        </form>
                    </div>
                </>
            )}
            {historyPopup.isOpen && (
                <HistoryPopup
                    isOpen={historyPopup.isOpen}
                    endpoint={historyConfig.endpoint}
                    queryParams={historyPopup.queryParams}
                    columns={historyConfig.columns}
                    onClose={closeHistoryPopup}
                />
            )}
            {changesPopup.isOpen && (
                <ChangesPopup
                    isOpen={changesPopup.isOpen}
                    date={changesPopup.date}
                    onClose={() => setChangesPopup({ isOpen: false, date: '' })}
                />
            )}
            {unscannedPopupOpen && (
                <UnscannedBagsPopup
                    isOpen={unscannedPopupOpen}
                    onClose={() => setUnscannedPopupOpen(false)}
                />
            )}
            <HeaderLogged />
            <Container className="scanners-inv-seeds-page">
                <div className="scannerform-inner active-formvar">
                    <form className="scannerform" onSubmit={handleForm1Submit}>
                        <h2 className="popup__title">Seeds Inventory Check</h2>
                        <h3 className="popup__subtitle">Please use the scanner to input data</h3>
                        <div className="scannerform-columns">
                            <div className="scannerform-column">
                                <div className="scannerform-inputgroup">
                                    <label className="scannerform-label">Seeds ID</label>
                                    <input
                                        className="scannerform__input"
                                        name="seedsID"
                                        value={form1State.seedsID}
                                        onChange={handleForm1Change}
                                        placeholder="Please scan the QR-code for seeds"
                                    />
                                </div>
                                <div
                                    className={`scannerform-inputgroup dynamic-label ${
                                        form1State.isLabelVisible ? 'visible-label' : 'hidden-label'
                                    }`}
                                    style={{ maxWidth: '250px' }}
                                >
                                    <label className="scannerform-label">
                                        Seeds Name - <span className="scannerform-dynlabel">{form1State.combinedSeedsName}</span>
                                    </label>
                                </div>
                                <div className="scannerform-inputgroup">
                                    <label className="scannerform-label">Bag Number</label>
                                    <textarea
                                        className="scannerform__textarea"
                                        name="bagnumid"
                                        value={form1State.bagnumid}
                                        onChange={handleForm1Change}
                                        placeholder="Enter bag numbers separated by commas, spaces, or new lines"
                                    />
                                    {form1BagNumError && <span style={{ color: 'red', margin: '5px' }}>{form1BagNumError}</span>}
                                </div>
                                <button type="submit" className="btn btn__green mt-15">Check Record</button>
                            </div>
                            <div className="scannerform-column scannerform-column--bags">
                                <label className="scannerform-label">Available Bags</label>
                                <div className="scannerform-inputgroup">
                                    <table className="scannerform-bags-table">
                                        <thead>
                                            <tr>
                                                <th className="scannerform-bags-th">Bag Number</th>
                                                <th className="scannerform-bags-th">Weight (lb)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {form1State.bagNumbers.length > 0 ? (
                                                form1State.bagNumbers.map((bag, index) => (
                                                    <tr key={index}>
                                                        <td className="scannerform-bags-td">{bag.bagnumid}</td>
                                                        <td className="scannerform-bags-td">{formatWeight(bag.currentWeight)}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="2" className="scannerform-bags-empty">No bags available</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>

                <div className="inventorylogtable-container">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2 className="inventorylogtable-heading">History</h2>
                        <button className="btn btn__green" onClick={openUnscannedPopup}>View Long Unscanned</button>
                    </div>
                    {logData.length === 0 ? (
                        <div className="inventorylogtable-no-history">No history yet</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="inventorylogtable-table">
                                <thead className="inventorylogtable-thead">
                                    <tr>
                                        <th className="inventorylogtable-th">Inventorization Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(groupedLogData)
                                        .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
                                        .map(([date, seeds]) => (
                                            <React.Fragment key={date}>
                                                <tr className="inventorylogtable-row">
                                                    <td className="inventorylogtable-td">
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                            <span className="flex items-center cursor-pointer" onClick={() => toggleDateExpansion(date)}>
                                                                <span className="inventorylogtable-expand-icon">
                                                                    {expandedSeeds.has(date) ? '▼' : '▶'}
                                                                </span>
                                                                {date}
                                                            </span>
                                                            <button onClick={(e) => { e.stopPropagation(); openChangesPopup(date); }}>
                                                                View Changes
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                {expandedSeeds.has(date) && (<>
                                                    <tr>
                                                        <td className="inventorylogtable-nested-container">
                                                            <table className="inventorylogtable-nested-table">
                                                                <thead className="inventorylogtable-nested-thead">
                                                                    <tr>
                                                                        <th className="inventorylogtable-th">Seeds Name</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {Object.entries(seeds)
                                                                        .sort(([_, a], [__, b]) =>
                                                                            a.combinedSeedsName.localeCompare(b.combinedSeedsName)
                                                                        )
                                                                        .map(([seedsID, { combinedSeedsName, bags }]) => (
                                                                            <React.Fragment key={seedsID}>
                                                                                <tr
                                                                                    className="inventorylogtable-row cursor-pointer"
                                                                                    onClick={() => toggleSeedExpansion(date, seedsID)}
                                                                                    ref={el => seedRowRefs.current[`${date}-${seedsID}`] = el}
                                                                                >
                                                                                    <td className="inventorylogtable-td">
                                                                                        <div className="seeds-name-container">
                                                                                            <span className="flex items-center">
                                                                                                <span className="inventorylogtable-expand-icon">
                                                                                                    {expandedSeeds.has(`${date}-${seedsID}`)
                                                                                                        ? '▼'
                                                                                                        : '▶'}
                                                                                                </span>
                                                                                                {combinedSeedsName}
                                                                                            </span>
                                                                                            <span className="pending-bags">
                                                                                                {getPendingBags(bags, date, seedsID).length > 0
                                                                                                    ? getPendingBags(bags, date, seedsID)
                                                                                                    : 'All bags scanned and confirmed'}
                                                                                            </span>
                                                                                        </div>
                                                                                    </td>
                                                                                </tr>
                                                                                {expandedSeeds.has(`${date}-${seedsID}`) && (
                                                                                    <tr>
                                                                                        <td className="inventorylogtable-subnested-container">
                                                                                            <table className="inventorylogtable-subnested-table">
                                                                                                <thead className="inventorylogtable-nested-thead">
                                                                                                    <tr>
                                                                                                        <th className="inventorylogtable-th">Bag Number</th>
                                                                                                        <th className="inventorylogtable-th">Container</th>
                                                                                                        <th className="inventorylogtable-th">Lot</th>
                                                                                                        <th className="inventorylogtable-th">Weight</th>
                                                                                                        <th className="inventorylogtable-th">Germ Rate</th>
                                                                                                        <th className="inventorylogtable-th">Germ Date</th>
                                                                                                         <th className="inventorylogtable-th">Status</th>
                                                                                                        <th className="inventorylogtable-th">Check Date</th>
                                                                                                        <th className="inventorylogtable-th">Was Scanned</th>
                                                                                                        <th className="inventorylogtable-th">Confirmed By</th>
                                                                                                        <th className="inventorylogtable-th text-center">
                                                                                                            Confirmed
                                                                                                        </th>
                                                                                                        <th className="inventorylogtable-th">Actions</th>
                                                                                                    </tr>
                                                                                                </thead>
                                                                                                <tbody>
                                                                                                    {bags
                                                                                                        .sort((a, b) => {
                                                                                                            if (a.wasScanned === b.wasScanned) {
                                                                                                                return a.bagnumid.localeCompare(b.bagnumid);
                                                                                                            }
                                                                                                            return a.wasScanned === 'Yes' ? -1 : 1;
                                                                                                        })
                                                                                                        .map((bag) => {
                                                                                                            const bagId = bag.id || `${bag.seedsID}-${bag.bagnumid}`;
                                                                                                            return (
                                                                                                                <tr key={bagId} className="inventorylogtable-row">
                                                                                                                    <td className="inventorylogtable-td">{bag.bagnumid}</td>
                                                                                                                    <td className="inventorylogtable-td">
                                                                                                                        {editingFields[`${bagId}-containerID`] ? (
                                                                                                                            <>
                                                                                                                                <select
                                                                                                                                    value={inputValues[`${bagId}-containerID`] || ''}
                                                                                                                                    onChange={(e) => setInputValues({ ...inputValues, [`${bagId}-containerID`]: e.target.value })}
                                                                                                                                >
                                                                                                                                    <option value="">Select Container</option>
                                                                                                                                    {containers.map((container) => (
                                                                                                                                        <option key={container.containerID} value={container.containerID}>
                                                                                                                                            {container.containerName}
                                                                                                                                        </option>
                                                                                                                                    ))}
                                                                                                                                </select>
                                                                                                                                <span className="save-icon" onClick={() => saveEdit(bagId, 'containerID')}>
                                                                                                                                    ✓
                                                                                                                                </span>
                                                                                                                            </>
                                                                                                                        ) : (
                                                                                                                            <>
                                                                                                                                {getContainerName(bag)}
                                                                                                                                <span className="edit-icon" onClick={() => startEditing(bagId, 'containerID')}>
                                                                                                                                    <i className="fa-solid fa-pen-to-square"></i>
                                                                                                                                </span>
                                                                                                                            </>
                                                                                                                        )}
                                                                                                                    </td>
                                                                                                                    <td className="inventorylogtable-td">
                                                                                                                        {editingFields[`${bagId}-old_lot`] ? (
                                                                                                                            <>
                                                                                                                                <input
                                                                                                                                    type="text"
                                                                                                                                    value={inputValues[`${bagId}-old_lot`] || ''}
                                                                                                                                    onChange={(e) => setInputValues({ ...inputValues, [`${bagId}-old_lot`]: e.target.value })}
                                                                                                                                />
                                                                                                                                <span className="save-icon" onClick={() => saveEdit(bagId, 'old_lot')}>
                                                                                                                                    ✓
                                                                                                                                </span>
                                                                                                                            </>
                                                                                                                        ) : (
                                                                                                                            <>
                                                                                                                                {getDisplayValue(bag, 'old_lot')}
                                                                                                                                <span className="edit-icon" onClick={() => startEditing(bagId, 'old_lot')}>
                                                                                                                                    <i className="fa-solid fa-pen-to-square"></i>
                                                                                                                                </span>
                                                                                                                            </>
                                                                                                                        )}
                                                                                                                    </td>
                                                                                                                    <td className="inventorylogtable-td">
                                                                                                                        {editingFields[`${bagId}-old_weight`] ? (
                                                                                                                            <>
                                                                                                                                <input
                                                                                                                                    type="number"
                                                                                                                                    value={inputValues[`${bagId}-old_weight`] || '0'}
                                                                                                                                    onChange={(e) => setInputValues({ ...inputValues, [`${bagId}-old_weight`]: e.target.value })}
                                                                                                                                />
                                                                                                                                <span className="save-icon" onClick={() => saveEdit(bagId, 'old_weight')}>
                                                                                                                                    ✓
                                                                                                                                </span>
                                                                                                                            </>
                                                                                                                        ) : (
                                                                                                                            <>
                                                                                                                                {getDisplayValue(bag, 'old_weight')}
                                                                                                                                <span className="edit-icon" onClick={() => startEditing(bagId, 'old_weight')}>
                                                                                                                                    <i className="fa-solid fa-pen-to-square"></i>
                                                                                                                                </span>
                                                                                                                            </>
                                                                                                                        )}
                                                                                                                    </td>
                                                                                                                    <td className="inventorylogtable-td">
                                                                                                                        {editingFields[`${bagId}-old_germtestrate`] ? (
                                                                                                                            <>
                                                                                                                                <input
                                                                                                                                    type="number"
                                                                                                                                    value={inputValues[`${bagId}-old_germtestrate`] || ''}
                                                                                                                                    onChange={(e) => setInputValues({ ...inputValues, [`${bagId}-old_germtestrate`]: e.target.value })}
                                                                                                                                />
                                                                                                                                <span className="save-icon" onClick={() => saveEdit(bagId, 'old_germtestrate')}>
                                                                                                                                    ✓
                                                                                                                                </span>
                                                                                                                            </>
                                                                                                                        ) : (
                                                                                                                            <>
                                                                                                                                {getDisplayValue(bag, 'old_germtestrate')}
                                                                                                                                <span className="edit-icon" onClick={() => startEditing(bagId, 'old_germtestrate')}>
                                                                                                                                    <i className="fa-solid fa-pen-to-square"></i>
                                                                                                                                </span>
                                                                                                                            </>
                                                                                                                        )}
                                                                                                                    </td>
                                                                                                                    <td className="inventorylogtable-td">
                                                                                                                        {editingFields[`${bagId}-old_germtestdate`] ? (
                                                                                                                            <>
                                                                                                                                <input
                                                                                                                                    type="date"
                                                                                                                                    value={inputValues[`${bagId}-old_germtestdate`] || ''}
                                                                                                                                    onChange={(e) => setInputValues({ ...inputValues, [`${bagId}-old_germtestdate`]: e.target.value })}
                                                                                                                                />
                                                                                                                                <span className="save-icon" onClick={() => saveEdit(bagId, 'old_germtestdate')}>
                                                                                                                                    ✓
                                                                                                                                </span>
                                                                                                                            </>
                                                                                                                        ) : (
                                                                                                                            <>
                                                                                                                                {formatUSADate(getDisplayValue(bag, 'old_germtestdate'))}
                                                                                                                                <span className="edit-icon" onClick={() => startEditing(bagId, 'old_germtestdate')}>
                                                                                                                                    <i className="fa-solid fa-pen-to-square"></i>
                                                                                                                                </span>
                                                                                                                            </>
                                                                                                                        )}
                                                                                                                    </td>
                                                                                                                    <td className="inventorylogtable-td">{bag.BagStatus}</td>
                                                                                                                    <td className="inventorylogtable-td">{formatUSADate(bag.inventorization_date)}</td>
                                                                                                                    <td className="inventorylogtable-td">
                                                                                                                        {bag.wasScanned}
                                                                                                                        {bag.wasScanned === 'No' && (
                                                                                                                            <span className="scan-icon" onClick={() => openScanPopup(bag.id, bag.bagnumid, bag.seedsID)}>
                                                                                                                                <i className="fa-solid fa-qrcode"></i>
                                                                                                                            </span>
                                                                                                                        )}
                                                                                                                    </td>
                                                                                                                    <td className="inventorylogtable-td">{bag.employeeName || '-'}</td>
                                                                                                                    <td className="inventorylogtable-td text-center">
                                                                                                                        <input
                                                                                                                            type="checkbox"
                                                                                                                            checked={checkboxStates[bagId] || false}
                                                                                                                            onChange={() => handleCheckboxChange(bagId)}
                                                                                                                        />
                                                                                                                    </td>
                                                                                                                    <td className="inventorylogtable-td">
                                                                                                                        <span className="rollback-icon" onClick={() => handleRollback(bag.id)}>
                                                                                                                            <i className="fa-solid fa-undo"></i>
                                                                                                                        </span>
                                                                                                                        <span className="history-icon" onClick={() => handleViewHistory(bag)}>
                                                                                                                            <i className="fa-solid fa-eye"></i>
                                                                                                                        </span>
                                                                                                                        <span className="trash-icon" onClick={() => handleTrashClick(bagId)}>
                                                                                                                            <i className="fa-solid fa-trash"></i>
                                                                                                                        </span>
                                                                                                                    </td>
                                                                                                                </tr>
                                                                                                            );
                                                                                                        })}
                                                                                                    {usedBags[`${date}-${seedsID}`] && usedBags[`${date}-${seedsID}`].map((bag) => {
                                                                                                        const bagId = bag.id || `${bag.seedsID}-${bag.bagnumid}`;
                                                                                                        return (
                                                                                                            <tr key={bagId} className="inventorylogtable-row used-bag">
                                                                                                                <td className="inventorylogtable-td">{bag.bagnumid}</td>
                                                                                                                <td className="inventorylogtable-td">
                                                                                                                    {editingFields[`${bagId}-containerID`] ? (
                                                                                                                        <>
                                                                                                                            <select
                                                                                                                                value={inputValues[`${bagId}-containerID`] || ''}
                                                                                                                                onChange={(e) => setInputValues({ ...inputValues, [`${bagId}-containerID`]: e.target.value })}
                                                                                                                            >
                                                                                                                                <option value="">Select Container</option>
                                                                                                                                {containers.map((container) => (
                                                                                                                                    <option key={container.containerID} value={container.containerID}>
                                                                                                                                        {container.containerName}
                                                                                                                                    </option>
                                                                                                                                ))}
                                                                                                                            </select>
                                                                                                                            <span className="save-icon" onClick={() => saveEdit(bagId, 'containerID')}>
                                                                                                                                ✓
                                                                                                                            </span>
                                                                                                                        </>
                                                                                                                    ) : (
                                                                                                                        <>
                                                                                                                            {getContainerName(bag)}
                                                                                                                            <span className="edit-icon" onClick={() => startEditing(bagId, 'containerID')}>
                                                                                                                                <i className="fa-solid fa-pen-to-square"></i>
                                                                                                                            </span>
                                                                                                                        </>
                                                                                                                    )}
                                                                                                                </td>
                                                                                                                <td className="inventorylogtable-td">
                                                                                                                    {editingFields[`${bagId}-old_lot`] ? (
                                                                                                                        <>
                                                                                                                            <input
                                                                                                                                type="text"
                                                                                                                                value={inputValues[`${bagId}-old_lot`] || ''}
                                                                                                                                onChange={(e) => setInputValues({ ...inputValues, [`${bagId}-old_lot`]: e.target.value })}
                                                                                                                            />
                                                                                                                            <span className="save-icon" onClick={() => saveEdit(bagId, 'old_lot')}>
                                                                                                                                ✓
                                                                                                                            </span>
                                                                                                                        </>
                                                                                                                    ) : (
                                                                                                                        <>
                                                                                                                            {getDisplayValue(bag, 'old_lot')}
                                                                                                                            <span className="edit-icon" onClick={() => startEditing(bagId, 'old_lot')}>
                                                                                                                                <i className="fa-solid fa-pen-to-square"></i>
                                                                                                                            </span>
                                                                                                                        </>
                                                                                                                    )}
                                                                                                                </td>
                                                                                                                <td className="inventorylogtable-td">
                                                                                                                    {editingFields[`${bagId}-old_weight`] ? (
                                                                                                                        <>
                                                                                                                            <input
                                                                                                                                type="number"
                                                                                                                                value={inputValues[`${bagId}-old_weight`] || ''}
                                                                                                                                onChange={(e) => setInputValues({ ...inputValues, [`${bagId}-old_weight`]: e.target.value })}
                                                                                                                            />
                                                                                                                            <span className="save-icon" onClick={() => saveEdit(bagId, 'old_weight')}>
                                                                                                                                ✓
                                                                                                                            </span>
                                                                                                                        </>
                                                                                                                    ) : (
                                                                                                                        <>
                                                                                                                            {getDisplayValue(bag, 'old_weight')}
                                                                                                                            <span className="edit-icon" onClick={() => startEditing(bagId, 'old_weight')}>
                                                                                                                                <i className="fa-solid fa-pen-to-square"></i>
                                                                                                                            </span>
                                                                                                                        </>
                                                                                                                    )}
                                                                                                                </td>
                                                                                                                <td className="inventorylogtable-td">
                                                                                                                    {editingFields[`${bagId}-old_germtestrate`] ? (
                                                                                                                        <>
                                                                                                                            <input
                                                                                                                                type="number"
                                                                                                                                value={inputValues[`${bagId}-old_germtestrate`] || ''}
                                                                                                                                onChange={(e) => setInputValues({ ...inputValues, [`${bagId}-old_germtestrate`]: e.target.value })}
                                                                                                                            />
                                                                                                                            <span className="save-icon" onClick={() => saveEdit(bagId, 'old_germtestrate')}>
                                                                                                                                ✓
                                                                                                                            </span>
                                                                                                                        </>
                                                                                                                    ) : (
                                                                                                                        <>
                                                                                                                            {getDisplayValue(bag, 'old_germtestrate')}
                                                                                                                            <span className="edit-icon" onClick={() => startEditing(bagId, 'old_germtestrate')}>
                                                                                                                                <i className="fa-solid fa-pen-to-square"></i>
                                                                                                                            </span>
                                                                                                                        </>
                                                                                                                    )}
                                                                                                                </td>
                                                                                                                <td className="inventorylogtable-td">
                                                                                                                    {editingFields[`${bagId}-old_germtestdate`] ? (
                                                                                                                        <>
                                                                                                                            <input
                                                                                                                                type="date"
                                                                                                                                value={inputValues[`${bagId}-old_germtestdate`] || ''}
                                                                                                                                onChange={(e) => setInputValues({ ...inputValues, [`${bagId}-old_germtestdate`]: e.target.value })}
                                                                                                                            />
                                                                                                                            <span className="save-icon" onClick={() => saveEdit(bagId, 'old_germtestdate')}>
                                                                                                                                ✓
                                                                                                                            </span>
                                                                                                                        </>
                                                                                                                    ) : (
                                                                                                                        <>
                                                                                                                            {formatUSADate(getDisplayValue(bag, 'old_germtestdate'))}
                                                                                                                            <span className="edit-icon" onClick={() => startEditing(bagId, 'old_germtestdate')}>
                                                                                                                                <i className="fa-solid fa-pen-to-square"></i>
                                                                                                                            </span>
                                                                                                                        </>
                                                                                                                    )}
                                                                                                                </td>
                                                                                                                <td className="inventorylogtable-td">{formatUSADate(bag.inventorization_date)}</td>
                                                                                                                <td className="inventorylogtable-td">
                                                                                                                    {bag.wasScanned}
                                                                                                                    {bag.wasScanned === 'No' && (
                                                                                                                        <span className="scan-icon" onClick={() => openScanPopup(bag.id, bag.bagnumid, bag.seedsID)}>
                                                                                                                            <i className="fa-solid fa-qrcode"></i>
                                                                                                                        </span>
                                                                                                                    )}
                                                                                                                </td>
                                                                                                                <td className="inventorylogtable-td">{bag.employeeName || '-'}</td>
                                                                                                                <td className="inventorylogtable-td text-center">
                                                                                                                    <input
                                                                                                                        type="checkbox"
                                                                                                                        checked={checkboxStates[bagId] || false}
                                                                                                                        onChange={() => handleCheckboxChange(bagId)}
                                                                                                                    />
                                                                                                                </td>
                                                                                                                <td className="inventorylogtable-td">
                                                                                                                    <span className="rollback-icon" onClick={() => handleRollback(bag.id)}>
                                                                                                                        <i className="fa-solid fa-undo"></i>
                                                                                                                    </span>
                                                                                                                    <span className="history-icon" onClick={() => handleViewHistory(bag)}>
                                                                                                                        <i className="fa-solid fa-eye"></i>
                                                                                                                    </span>
                                                                                                                    <span className="trash-icon" onClick={() => handleTrashClick(bagId)}>
                                                                                                                        <i className="fa-solid fa-trash"></i>
                                                                                                                    </span>
                                                                                                                </td>
                                                                                                            </tr>
                                                                                                        );
                                                                                                    })}
                                                                                                </tbody>
                                                                                            </table>
                                                                                            <div>
                                                                                                <button className="toggle-used-bags-button" onClick={() => toggleUsedBags(date, seedsID)}>
                                                                                                    {usedBags[`${date}-${seedsID}`] ? 'Hide Used Bags' : 'Show Used Bags'}
                                                                                                </button>
                                                                                            </div>
                                                                                            <div className="confirm-button-container">
                                                                                                <div className="signature-container">
                                                                                                    <label>Signature:</label>
                                                                                                    <select
                                                                                                        value={selectedEmployee}
                                                                                                        onChange={(e) => setSelectedEmployee(e.target.value)}
                                                                                                    >
                                                                                                        <option value="">Select Employee</option>
                                                                                                        {employees.map((employee) => (
                                                                                                            <option key={employee.employeeID} value={employee.employeeID}>
                                                                                                                {employee.employeeName}
                                                                                                            </option>
                                                                                                        ))}
                                                                                                    </select>
                                                                                                </div>
                                                                                                <button className="confirm-button" onClick={() => handleConfirm(date, seedsID)}>
                                                                                                    Confirm
                                                                                                </button>
                                                                                            </div>
                                                                                        </td>
                                                                                    </tr>
                                                                                )}
                                                                            </React.Fragment>
                                                                        ))}
                                                                </tbody>
                                                            </table>
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="inventorylogtable-nested-container">
                                                            <table className="inventorylogtable-nested-table">
                                                                <thead className="inventorylogtable-nested-thead">
                                                                    <tr>
                                                                        <th
                                                                            className="inventorylogtable-th"
                                                                            colSpan="5"
                                                                            style={{ cursor: 'pointer', userSelect: 'none' }}
                                                                            onClick={() => toggleWholeSeedsExpansion(date)}
                                                                        >
                                                                            <span className="inventorylogtable-expand-icon">
                                                                                {expandedWholeSeeds.has(date) ? '▼' : '▶'}
                                                                            </span>
                                                                            Whole Seeds
                                                                        </th>
                                                                    </tr>
                                                                </thead>
                                                                {expandedWholeSeeds.has(date) && (
                                                                    <tbody>
                                                                        {wholeSeedsData[date]?.loading ? (
                                                                            <tr><td colSpan="5" style={{ textAlign: 'center', padding: '12px' }}>Loading...</td></tr>
                                                                        ) : (
                                                                            <>
                                                                                <tr>
                                                                                    <td colSpan="5" style={{ padding: '6px 12px', fontWeight: 'bold', background: '#d1fae5' }}>
                                                                                        Scanned ({wholeSeedsData[date]?.scanned.length || 0})
                                                                                    </td>
                                                                                </tr>
                                                                                {wholeSeedsData[date]?.scanned.length > 0 ? (
                                                                                    <>
                                                                                        <tr className="inventorylogtable-row">
                                                                                            <th className="inventorylogtable-th">Seeds Name</th>
                                                                                            <th className="inventorylogtable-th">Bag #</th>
                                                                                            <th className="inventorylogtable-th">Container</th>
                                                                                            <th className="inventorylogtable-th">Lot</th>
                                                                                            <th className="inventorylogtable-th">Weight</th>
                                                                                        </tr>
                                                                                        {wholeSeedsData[date].scanned.map(bag => (
                                                                                            <tr key={`ws-s-${bag.seedsID}-${bag.bagnumid}`} className="inventorylogtable-row">
                                                                                                <td className="inventorylogtable-td">{bag.combinedSeedsName}</td>
                                                                                                <td className="inventorylogtable-td">{bag.bagnumid}</td>
                                                                                                <td className="inventorylogtable-td">{bag.containerName || '-'}</td>
                                                                                                <td className="inventorylogtable-td">{bag.lot || '-'}</td>
                                                                                                <td className="inventorylogtable-td">{formatWeight(bag.weight)}</td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </>
                                                                                ) : (
                                                                                    <tr><td colSpan="5" style={{ padding: '6px 12px', color: '#6b7280' }}>No scanned bags</td></tr>
                                                                                )}
                                                                                <tr>
                                                                                    <td colSpan="5" style={{ padding: '6px 12px', fontWeight: 'bold', background: '#fee2e2' }}>
                                                                                        Not Scanned ({wholeSeedsData[date]?.notScanned.length || 0})
                                                                                    </td>
                                                                                </tr>
                                                                                {wholeSeedsData[date]?.notScanned.length > 0 ? (
                                                                                    <>
                                                                                        <tr className="inventorylogtable-row">
                                                                                            <th className="inventorylogtable-th">Seeds Name</th>
                                                                                            <th className="inventorylogtable-th">Bag #</th>
                                                                                            <th className="inventorylogtable-th">Container</th>
                                                                                            <th className="inventorylogtable-th">Lot</th>
                                                                                            <th className="inventorylogtable-th">Weight</th>
                                                                                        </tr>
                                                                                        {wholeSeedsData[date].notScanned.map(bag => (
                                                                                            <tr key={`ws-n-${bag.seedsID}-${bag.bagnumid}`} className="inventorylogtable-row">
                                                                                                <td className="inventorylogtable-td">{bag.combinedSeedsName}</td>
                                                                                                <td className="inventorylogtable-td">{bag.bagnumid}</td>
                                                                                                <td className="inventorylogtable-td">{bag.containerName || '-'}</td>
                                                                                                <td className="inventorylogtable-td">{bag.lot || '-'}</td>
                                                                                                <td className="inventorylogtable-td">{formatWeight(bag.weight)}</td>
                                                                                            </tr>
                                                                                        ))}
                                                                                    </>
                                                                                ) : (
                                                                                    <tr><td colSpan="5" style={{ padding: '6px 12px', color: '#6b7280' }}>No unscanned bags</td></tr>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </tbody>
                                                                )}
                                                            </table>
                                                        </td>
                                                    </tr>
                                                </>)}
                                            </React.Fragment>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </Container>
        </div>
    );
};

export default ScannersSeedStockComparsion;